/**
 * Hindi message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: formal-polite आप (locked in l10n/locales.md);
 * imperatives take the -एँ / -िए form (भेजें, खोलें).
 *
 * Platform terms follow modern Google-hi UI conventions (थीम for theme,
 * बातचीत for conversation, पेयर करना for pair, हटाएँ for dismiss,
 * डिसकनेक्ट करें for disconnect); "Glance" is the untranslated feature
 * brand name (glossary D1) and "क्लिप" is the loanword for clip (D2,
 * rendered in Devanagari). "receiver" surface = रिसीवर.
 *
 * Plural notes: hi is a {one, other} CLDR locale whose `one` category
 * covers BOTH 0 and 1, so count-bearing bodies always interpolate the
 * count and never hardcode a number. Many Hindi count-nouns are invariant
 * across both forms (डिवाइस, संदेश, कॉल, वर्ण, मिलान); they still route
 * through `selectPlural("hi", …)` to keep the shape identical to en.
 * सूचना / सूचनाएँ genuinely inflects.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "कनेक्ट है",
    statusReconnecting: "फिर से कनेक्ट हो रहा है…",
    statusDisconnected: "कनेक्ट नहीं है",
    openSettings: "सेटिंग खोलें",
    pairedFallbackDevice: "आपका फ़ोन",
    publicModeToggleEnable: "सूचना सामग्री छिपाएँ",
    publicModeToggleDisable: "सूचना सामग्री दिखाएँ",
  },

  notifications: {
    replyAriaLabel: "जवाब दें",
    replyPlaceholder: "जवाब…",
    replySend: "भेजें",
    sent: "भेज दिया",
    failedToSend: "भेजने में विफल",
    unread: "अपठित",
    dismiss: "हटाएँ",
    notificationImageAlt: "सूचना इमेज",
  },

  conversations: {
    replyAriaLabel: "जवाब दें",
    replyPlaceholder: "जवाब…",
    replySend: "भेजें",
    sent: "भेज दिया",
    failedToSend: "भेजने में विफल",
    dismiss: "बातचीत हटाएँ",
    messagesListLabel: "इस बातचीत के संदेश",
    deviceOwner: "आप",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "कोई अपठित संदेश नहीं"
        : `${count} ${selectPlural("hi", count, {
            one: "अपठित संदेश",
            other: "अपठित संदेश",
          })}`,
  },

  list: {
    today: "आज",
    yesterday: "कल",
    searchNotifications: "सूचनाएँ खोजें",
    clearSearch: "खोज हटाएँ",
    clearAll: "सभी हटाएँ",
    markAllRead: "सभी को पढ़ा हुआ मार्क करें",
    groupCheckboxLabel: "ग्रुप",
    noNotificationsTitle: "अभी तक कोई सूचना नहीं",
    noNotificationsDesc: "आपके फ़ोन से मिरर हुए नए संदेश यहाँ दिखेंगे.",
    noMatchesTitle: "कोई मेल नहीं",
    notificationsList: "सूचनाएँ",
    privacyAppList: "ऐप के हिसाब से सूचनाएँ",
    clear: "हटाएँ",
    // "मिलान" is invariant across Hindi plural forms; count still routes
    // through selectPlural to keep the shape identical to en.
    clearMatches: (count: number): string =>
      `${count} ${selectPlural("hi", count, { one: "मिलान", other: "मिलान" })} हटाएँ`,
    noMatchesDesc: (query: string): string => `“${query}” से कोई सूचना मेल नहीं खाती.`,
    appNotificationsGroup: (appName: string): string => `${appName} सूचनाएँ`,
    dismissAllFrom: (appName: string): string => `${appName} की सभी हटाएँ`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("hi", count, { one: "संदेश", other: "संदेश" })
          : kind === "calls"
            ? selectPlural("hi", count, { one: "कॉल", other: "कॉल" })
            : selectPlural("hi", count, { one: "सूचना", other: "सूचनाएँ" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "हटाएँ",
    kbdNavigate: "नेविगेट करें",
    kbdOrSeparator: "या",
  },

  settings: {
    back: "सूचनाओं पर वापस",
    settings: "सेटिंग",
    general: "सामान्य",
    theme: "थीम",
    themeAuto: "ऑटो",
    themeLight: "लाइट",
    themeDark: "डार्क",
    language: "भाषा",
    languageSystemDefault: "सिस्टम डिफ़ॉल्ट",
    notificationsSection: "सूचनाएँ",
    osNotifications: "OS सूचनाएँ",
    callNotifications: "कॉल सूचनाएँ",
    privacySecurity: "गोपनीयता",
    analyticsToggle: "अनाम उपयोग आँकड़े",
    analyticsToggleDescription: "सिर्फ़ गिनती और मील के पत्थर — आपकी सूचनाएँ कभी नहीं.",
    statusConnected: "कनेक्ट है",
    statusReconnecting: "फिर से कनेक्ट हो रहा है…",
    statusDisconnected: "कनेक्ट नहीं है",
    disconnectFromPhone: "फ़ोन से डिसकनेक्ट करें",
    disconnectConfirm: "क्या आप पक्का करना चाहते हैं? इससे आपके डिवाइस की पेयरिंग हट जाएगी.",
    yesDisconnect: "हाँ, डिसकनेक्ट करें",
    cancel: "रद्द करें",
    version: "वर्शन",
    encryption: "एन्क्रिप्शन",
    encryptionValue: "E2E (NaCl)",
    needHelp: "मदद चाहिए? __EMAIL__ पर ईमेल करें",
    disconnectFailed: "डिसकनेक्ट नहीं हो सका — कृपया फिर से कोशिश करें",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `डिसकनेक्ट हो गया, पर लोकल क्लीनअप विफल: ${errorMsg}. कृपया पेज फिर से लोड करें.`,
    disconnectLocalCleanupFailed: "डिसकनेक्ट हो गया, पर लोकल क्लीनअप विफल. कृपया पेज फिर से लोड करें.",
  },

  pairing: {
    enterCodeInstruction: "यह कोड Reflecto ऐप में डालें",
    pairedSuccessfully: "पेयरिंग सफल!",
    pairingFailed: "पेयरिंग विफल",
    requestingPairingCode: "पेयरिंग कोड मांगा जा रहा है…",
    codeExpired: "कोड खत्म हो गया",
    tryAgain: "फिर से कोशिश करें",
    failedToStartPairing: "पेयरिंग शुरू नहीं हो सकी",
    installHeading: "अभी तक ऐप नहीं है?",
    installScanHint: "अपने फ़ोन के कैमरे से स्कैन करें",
    installQrLabel: "फ़ोन पर Reflecto इंस्टॉल करने के लिए QR कोड",
    capabilitiesHeading: "पेयरिंग के बाद आप:",
    capabilityMirror: "अपने फ़ोन की नोटिफ़िकेशन यहाँ देख सकते हैं",
    capabilityReply: "फ़ोन उठाए बिना जवाब दे सकते हैं",
    capabilityShare: "क्लिपबोर्ड और फ़ाइलें दोनों तरफ़ भेज सकते हैं",
    encryptionNote: "एंड-टू-एंड एन्क्रिप्टेड. सर्वर आपका कॉन्टेंट कभी नहीं देखता.",
  },

  dnd: {
    title: "परेशान न करें",
    description: "इस डिवाइस पर अगले एक घंटे के लिए सूचनाएँ रोकें.",
    snooze1h: "1 घंटे के लिए रोकें",
    snoozeActiveUntil: (formattedTime: string): string => `${formattedTime} तक चालू`,
    cancelSnooze: "रद्द करें",
  },

  connectors: {
    title: "कनेक्ट की गई सेवाएँ",
    emptyBody:
      "अभी तक कोई कनेक्ट की गई सेवा नहीं. ChatGPT या Claude को उनकी कनेक्टर सेटिंग से कनेक्ट करें, फिर अपने फ़ोन पर कोड की पुष्टि करें.",
    scopeSend: "सूचनाएँ भेज सकता है",
    added: (date: string): string => `जोड़ा गया ${date}`,
    lastUsed: (relative: string): string => `आखिरी बार इस्तेमाल ${relative}`,
    neverUsed: "कभी इस्तेमाल नहीं हुआ",
    revoke: "डिसकनेक्ट करें",
    revokeConfirm: (label: string): string => `${label} को डिसकनेक्ट करें? इसका ऐक्सेस तुरंत खत्म हो जाएगा.`,
    revokeAction: "डिसकनेक्ट करें",
    revokeConfirmLabel: (label: string): string => `${label} को डिसकनेक्ट करें`,
    cancel: "रद्द करें",
    loadError: "कनेक्ट की गई सेवाएँ लोड नहीं हो सकीं. कृपया फिर से कोशिश करें.",
    revokeError: "सेवा डिसकनेक्ट नहीं हो सकी. कृपया फिर से कोशिश करें.",
    retry: "फिर से कोशिश करें",
  },

  safetyNumber: {
    rowLabel: "एन्क्रिप्शन सत्यापित करें",
    rowSublabel: "अपने फ़ोन के साथ सुरक्षा संख्याओं की तुलना करें",
    cardTitle: "सुरक्षा संख्या",
    caption: (deviceLabel: string): string =>
      `${deviceLabel} पर दिखाई देने वाली संख्या के साथ तुलना करें. अगर दोनों मेल खाएँ, तो आपकी एंड-टू-एंड एन्क्रिप्शन सुरक्षित है.`,
    repairNote: "यह संख्या डिवाइस को फिर से पेयर करने पर बदल जाती है.",
    copy: "कॉपी करें",
    copied: "कॉपी किया गया",
    maskedHint: "गोपनीयता मोड चालू होने पर छुपा हुआ",
  },

  feedback: {
    title: "फ़ीडबैक भेजें",
    close: "बंद करें",
    messageLabel: "आपका संदेश",
    messagePlaceholder: "कोई बग मिला, कोई आइडिया है, या कुछ ठीक नहीं लग रहा? हमें बताएँ.",
    categoryLabel: "श्रेणी",
    categoryBug: "बग",
    categoryIdea: "आइडिया",
    categoryOther: "अन्य",
    emailLabel: "ईमेल (वैकल्पिक, सिर्फ़ तभी जब आप जवाब चाहें)",
    emailPlaceholder: "you@example.com",
    emailHelp: "हम आपको किसी सूची में नहीं जोड़ेंगे.",
    disclosure:
      "आपका संदेश Reflecto के सर्वर पर भेजा जाता है और हमारे इंटरनल इशू ट्रैकर में सेव किया जाता है. रिपोर्ट को आपस में जोड़ने के लिए हम आपके ऐप वर्शन और पेयरिंग के दौरान दी गई एक डिवाइस ID जोड़ते हैं. हम आपकी सूचनाएँ, संपर्क या कोई निजी डेटा नहीं देखते. ईमेल वैकल्पिक है — इसे सिर्फ़ तभी दें जब आप जवाब चाहें. हम आपको किसी सूची में नहीं जोड़ते.",
    submit: "फ़ीडबैक भेजें",
    sending: "भेजा जा रहा है…",
    success: "धन्यवाद — इसे कोई व्यक्ति पढ़ता है, आम तौर पर 24 घंटे के भीतर",
    error: "भेजा नहीं जा सका. कृपया फिर से कोशिश करें.",
    done: "हो गया",
    sendFeedback: "फ़ीडबैक भेजें",
  },

  uninstall: {
    headline: "Reflecto का एक्सटेंशन अनइंस्टॉल हो गया",
    body: "कभी कोई अकाउंट था ही नहीं, और एक्सटेंशन आपकी पेयरिंग इसी कंप्यूटर पर रखता था, इसलिए साफ़ करने के लिए कुछ भी बाकी नहीं है.",
    ask: "अगर आपके पास तीस सेकंड हैं, तो हम जानना चाहेंगे कि क्या गड़बड़ हुई.",
    categoryLegend: "यह किस तरह का फ़ीडबैक है?",
    categoryBug: "कुछ काम करना बंद हो गया",
    categoryIdea: "मुझे जो चाहिए था वह इसमें नहीं था",
    categoryOther: "कुछ और",
    messageLabel: "क्या गड़बड़ हुई?",
    messagePlaceholder: "कुछ घंटों बाद सूचनाएँ आना बंद हो गईं.",
    messageRequired: "पहले एक वाक्य लिखें, फिर भेजें.",
    charactersLeft: (count: number): string => `${count} वर्ण बाकी`,
    emailLabel: "ईमेल (वैकल्पिक)",
    emailPlaceholder: "you@example.com",
    emailHelp: "सिर्फ़ आपको जवाब देने के लिए. और कुछ नहीं.",
    submit: "फ़ीडबैक भेजें",
    sending: "भेजा जा रहा है…",
    retry: "फिर से कोशिश करें",
    privacyNote:
      "यह फ़ॉर्म किसी डिवाइस से जुड़ा नहीं है. इसमें आपकी कोई सूचना, क्लिपबोर्ड या फ़ाइलें नहीं हैं. ईमेल वैकल्पिक है और सिर्फ़ जवाब देने के लिए इस्तेमाल होता है.",
    success: "धन्यवाद. इनमें से हर एक को कोई व्यक्ति पढ़ता है.",
    errorGeneric: "यह भेजा नहीं जा सका. आपका संदेश यहीं है.",
    errorRateLimited: "आपने हाल ही में कई संदेश भेजे हैं. करीब एक घंटे बाद फिर कोशिश करें. आपका टेक्स्ट यहीं रहेगा.",
    footerWebApp: "Reflecto का वेब ऐप एक्सटेंशन के बिना भी काम करता है.",
    footerWebAppLink: "वेब ऐप खोलें",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "बंद करें",
    aiSummaryTag: "AI सारांश",
    errorAi: "अभी सारांश नहीं बन सका.",
    retryCta: "फिर से कोशिश करें",
    downloadHeading: "AI सारांश पाएँ",
    downloadBody:
      "आपने जो छूटा, उसे डिवाइस पर ही चलने वाले AI रीकैप से जानें. यह पूरी तरह आपके डिवाइस पर चलता है.",
    downloadCta: "मॉडल डाउनलोड करें",
    downloadPrivacyNote: "एक बार का डाउनलोड. सर्वर आपकी सूचनाएँ कभी नहीं देखता.",
    downloadingHeading: "मॉडल डाउनलोड हो रहा है…",
    caughtUp: "आप पूरी तरह अप-टू-डेट हैं — अभी किसी चीज़ को आपके ध्यान की ज़रूरत नहीं.",
    needsYou: "आपका ध्यान चाहिए",
    quiet: "अन्य",
    scanning: (pending: number): string => `${pending} का सारांश बन रहा है…`,
    jump: "सूचना खोलें",
    affordanceReply: "जवाब दें",
    affordanceOpen: "खोलें",
    affordanceCopyOtp: "कोड कॉपी करें",
    buttonLabel: "Glance",
  },

  update: {
    title: "अपडेट उपलब्ध",
    description: "Reflecto का नया वर्शन तैयार है. अपडेट करने के लिए रीलोड करें.",
    reload: "रीलोड करें",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `हमने इस डिवाइस का नाम “${name}” रखा.`,
    cardLooksGood: "ठीक है",
    cardEdit: "बदलें",
    cardAriaLabel: "डिवाइस का नाम पक्का करें",
    renameTitle: "इस डिवाइस का नाम बदलें",
    renameLabel: "डिवाइस का नाम",
    renameSave: "सेव करें",
    renameCancel: "रद्द करें",
    renameFailed: "नाम नहीं बदल सका. कृपया फिर से कोशिश करें.",
  },

  collision: {
    title: (name: string): string => `आपके पास पहले से एक “${name}” है`,
    subtitle: "इसे हम क्या नाम दें?",
    chipWork: (name: string): string => `वर्क ${name}`,
    chipHome: (name: string): string => `होम ${name}`,
    chipPersonal: (name: string): string => `निजी ${name}`,
    customLabel: "कस्टम नाम",
    save: "सेव करें",
    useAnyway: "फिर भी मूल नाम इस्तेमाल करें",
    saveFailed: "नया नाम सेव नहीं हो सका. कृपया फिर से कोशिश करें.",
    ariaLabel: "डुप्लिकेट डिवाइस नाम सुलझाएँ",
  },

  clipboard: {
    title: "क्लिपबोर्ड",
    emptyTitle: "अभी कोई क्लिप नहीं",
    empty: "आपके दूसरे डिवाइस के क्लिप यहाँ दिखते हैं. कॉपी करने के लिए टैप करें.",
    from: (label: string): string => `${label} से`,
    to: (label: string): string => `${label} को`,
    copy: "क्लिपबोर्ड पर कॉपी करें",
    copied: "कॉपी हो गया",
    reveal: "दिखाएँ",
    hide: "छिपाएँ",
    maskedHint: "सामग्री छिपी है — इसे देखने के लिए दिखाएँ दबाएँ",
    sensitiveChip: "संवेदनशील",
    cloudChip: "आपके API टोकन से",
    delete: "क्लिप हटाएँ",
    sendPlaceholder: "भेजने के लिए टेक्स्ट पेस्ट करें…",
    sendCta: "भेजें",
    sendSensitiveToggle: "संवेदनशील मार्क करें",
    sendSuccess: (n: number): string =>
      `${n} ${selectPlural("hi", n, { one: "डिवाइस", other: "डिवाइस" })} पर भेजा गया.`,
    sendPartial: (n: number, total: number): string =>
      `${total} में से ${n} ${selectPlural("hi", total, { one: "डिवाइस", other: "डिवाइस" })} तक पहुँचा.`,
    sendNoDevices: "भेजने के लिए कोई पेयर किया गया डिवाइस नहीं.",
    sendFailed: "भेजा नहीं जा सका. कृपया फिर से कोशिश करें.",
    sendEmpty: "भेजने के लिए कुछ नहीं.",
    dockRecentHeading: "हाल के",
    dockEmptyClipboard: "आपका क्लिपबोर्ड खाली है.",
    clearAll: "सभी हटाएँ",
    clearAllConfirm: "हाँ, सभी हटाएँ",
    clearAllCancel: "रद्द करें",
    shareTitle: "अपने डिवाइस पर भेजें",
    shareReview:
      "आप जो भेज रहे हैं उसकी जाँच कर लें. यह एंड-टू-एंड एन्क्रिप्टेड होकर आपके पेयर किए गए डिवाइस पर जाता है.",
    shareSend: "भेजें",
    shareCancel: "रद्द करें",
    shareNotPaired: "शेयर किया गया टेक्स्ट भेजने के लिए पहले कोई डिवाइस पेयर करें.",
    shareOpenApp: "Reflecto खोलें",
    pasteFromClipboard: "क्लिपबोर्ड से पेस्ट करें",
    readFailed: "आपका क्लिपबोर्ड नहीं पढ़ा जा सका — नीचे पेस्ट करें।",
  },
  install: {
    title: "Reflecto इंस्टॉल करें",
    body: "तेज़ ऐक्सेस के लिए इसे अपनी होम स्क्रीन पर जोड़ें.",
    cta: "इंस्टॉल करें",
    dismissAria: "इंस्टॉल प्रॉम्प्ट खारिज करें",
  },
  nav: {
    home: "होम",
    clipboard: "क्लिपबोर्ड",
    files: "फ़ाइलें",
    ariaLabel: "मुख्य",
    fileHiddenPublicMode: "निजता मोड चालू रहने तक छिपा हुआ।",
  },
  common: {
    loading: "लोड हो रहा है",
  },
  errors: {
    notPaired: "पेयर नहीं है",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${os} पर ${browser}`,
    webFallback: "वेब ब्राउज़र",
  },

  palette: {
    placeholder: "कमांड लिखें…",
    noResults: "कोई मिलती-जुलती कमांड नहीं",
    ariaLabel: "कमांड पैलेट",
    clearAll: "सभी सूचनाएँ हटाएँ",
    glance: "Glance सारांश",
    cancelSnooze: "रोक रद्द करें",
    copyClip: "अंतिम प्राप्त क्लिप कॉपी करें",
  },

  fileTransfer: {
    sendTitle: "फ़ाइल भेजें",
    sendChooseFile: "फ़ाइल चुनें",
    sendDeviceLabel: "किसे भेजें",
    sendPhoneFallbackName: "फ़ोन",
    send: "फ़ाइल भेजें",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "तैयारी हो रही है…" : `तैयारी हो रही है — ${pct}%`,
    sendDelivering: "आपके फ़ोन पर पहुँचाया जा रहा है…",
    sendWaitingAccept: "आपके फ़ोन के स्वीकार करने का इंतज़ार है…",
    sendWaitingAppOpen: "स्वीकार करने के लिए अपने फ़ोन में Reflecto खोलें",
    sendWaitingAppOpenHint:
      "आपका फ़ोन पुश सूचनाएँ प्राप्त नहीं कर सकता, इसलिए यह फ़ाइल तभी दिखेगी जब Reflecto खुला होगा।",
    sendUploading: (partNumber: number, partCount: number): string =>
      `${partCount} में से भाग ${partNumber} अपलोड हो रहा है…`,
    sendDone: "भेज दिया गया.",
    sendFailedRejected: "आपके फ़ोन ने फ़ाइल अस्वीकार कर दी.",
    sendFailedNoAnswer: "आपके फ़ोन से कोई जवाब नहीं मिला.",
    sendFailedUploadFailed: "अपलोड नहीं हो सका. कृपया फिर से कोशिश करें.",
    sendFailedCancelled: "रद्द कर दिया गया.",
    sendFailedGeneric: "कुछ गड़बड़ हो गई. कृपया फिर से कोशिश करें.",
    sendTooLarge: (maxMiB: number): string => `फ़ाइल बहुत बड़ी है (अधिकतम ${maxMiB} MiB).`,
    sendKeepOpenHint: "ट्रांसफ़र पूरा होने तक इस टैब को खुला रखें.",
    sendAnother: "एक और भेजें",
    sendNoDevice: "फ़ाइलें भेजने के लिए पहले कोई फ़ोन पेयर करें.",
    sendCancel: "रद्द करें",
    sendPeerProgress: (chunksDone: number): string => `फ़ोन को ${chunksDone} खंड मिल चुके हैं.`,

    receiveTitle: "आने वाली फ़ाइलें",
    receiveOfferFrom: (sender: string): string => `${sender} आपको एक फ़ाइल भेजना चाहता है`,
    receiveAccept: "स्वीकार करें",
    receiveDecline: "अस्वीकार करें",
    receiveReceiving: (n: number, total: number): string => `${total} में से भाग ${n} मिल रहा है…`,
    receiveDone: "सेव हो गई.",
    receiveFailed: "ट्रांसफ़र नहीं हो सका.",
    receiveCancelled: "रद्द कर दिया गया.",
    receiveCancel: "रद्द करें",

    errorChunkAuthFail: "ट्रांसफ़र नहीं हो सका — डेटा की पुष्टि नहीं हो पाई. फिर से कोशिश करें.",
    errorHashMismatch: "ट्रांसफ़र नहीं हो सका — फ़ाइल की इंटीग्रिटी जाँच पास नहीं हुई. फिर से कोशिश करें.",
    errorStorageFull: "इस ट्रांसफ़र के लिए पर्याप्त स्टोरेज नहीं है.",
    errorExpired: "ट्रांसफ़र की समय-सीमा खत्म हो गई.",
    errorQuotaExceeded: "आज की ट्रांसफ़र सीमा पूरी हो गई — कल फिर से कोशिश करें.",
    errorInternal: "ट्रांसफ़र नहीं हो सका — कुछ गड़बड़ हो गई.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "हटाएँ",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "ट्रांसफ़र की प्रगति",
    outgoing: (n: number): string => `भेजे जा रहे (${n})`,
    incoming: (n: number): string => `आ रहे (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "सभी स्वीकारें",
    dropTitle: "यहाँ खींचकर छोड़ें",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `${size} तक की फ़ाइलें.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "यहाँ खींचकर छोड़ें, या भेजने के लिए फ़ाइल चुनें",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "सीधा",
    transportDirectHint: "डिवाइस से डिवाइस भेजा गया — हमारे सर्वर पर कभी संग्रहीत नहीं।",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "सर्वर के ज़रिए",
    transportRelayHint: "हमारे सर्वर से रिले किया गया, फिर भी एंड-टू-एंड एन्क्रिप्टेड — हम इसे पढ़ नहीं सकते।",
    directBadge: "डायरेक्ट",
  },
  time: {
    justNow: "अभी-अभी",
    minutesAgo: (count: number): string => `${count} मि॰ पहले`,
    hoursAgo: (count: number): string => `${count} घं॰ पहले`,
  },
} satisfies MessagesShape;
